import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BsGear, BsGripVertical, BsPlusLg, BsThreeDotsVertical, BsTrash } from 'react-icons/bs';
import { v4 as uuid } from 'uuid';
import FormField from './FormField';
import { discardSetImage } from './api/setsApi';

const clone = (value) => JSON.parse(JSON.stringify(value));
const featureDefinition = (moduleDefinition, type) => moduleDefinition?.features?.find((item) => item.type === type);
const featureName = (feature, definition, index) => feature.data?.title?.trim()
  || feature.data?.hptitle?.trim()
  || `${definition?.title || 'Feature'} ${index + 1}`;
const defaultsFrom = (fields = []) => fields.reduce((defaults, field) => ({
  ...defaults,
  ...(field.defaults || {}),
  [field.key]: field.default ?? field.defaultValue ?? '',
}), {});
const isCustomClass = (name) => typeof name === 'string' && /^(?:hc|ug)_/i.test(name.trim());
const customClassLabel = (name) => name
  .trim()
  .replace(/^(?:hc|ug)_/i, '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Custom Class';

const fieldGroup = (field) => {
  const key = field.key.toLowerCase();
  if (field.type === 'image' || field.type === 'image-selector') return 'media';
  if (field.type === 'colorpicker' || field.type === 'slider' || /(align|position|columns|theme|fit|size|darkness|gradient|crop|autoplay)/.test(key)) return 'appearance';
  if (field.type === 'cards' || field.type === 'deckcards' || field.type === 'heropower') return 'cards';
  if (/(background|logo|image|media|portrait|video)/.test(key)) return 'media';
  return 'content';
};

const GROUPS = { content: 'Content', media: 'Media', cards: 'Cards', appearance: 'Appearance' };

const effectiveFieldValue = (field, values) => {
  if (values[field.key] !== undefined && values[field.key] !== '') return values[field.key];
  const inferred = Object.entries(field.autoFrom || {}).find(([, valueKey]) => Boolean(values[valueKey]));
  return inferred?.[0] || field.default || field.defaultValue || '';
};

const fieldIsVisible = (field, fields, values) => {
  if (!field.visibleWhen) return true;
  const controller = fields.find((item) => item.key === field.visibleWhen.key) || { key: field.visibleWhen.key };
  const current = effectiveFieldValue(controller, values);
  return field.visibleWhen.in ? field.visibleWhen.in.includes(current) : current === field.visibleWhen.value;
};

function EditorFields({ fields, values, onChange, setId, onAssetUploaded }) {
  const visibleFields = fields.filter((field) => fieldIsVisible(field, fields, values));
  const groups = visibleFields.reduce((result, field) => {
    const group = fieldGroup(field);
    result[group] = [...(result[group] || []), field];
    return result;
  }, {});
  return <div className="set-field-groups">{Object.keys(GROUPS).filter((key) => groups[key]?.length).map((group) => <section className={`set-field-group set-field-group-${group}`} key={group}><h4>{GROUPS[group]}</h4><div className="set-field-group-body">{groups[group].map((field) => <FormField key={field.key} fieldDef={field} currentValue={effectiveFieldValue(field, values)} onChange={(value) => onChange(field.key, value)} onChangeField={onChange} allValues={values} setId={setId} onAssetUploaded={onAssetUploaded} />)}</div></section>)}</div>;
}

export default function ModuleEditModal({ show, handleClose, handleSave, moduleDefinition, moduleData, setId }) {
  const [currentData, setCurrentData] = useState({});
  const [currentFeatures, setCurrentFeatures] = useState([]);
  const [activePanel, setActivePanel] = useState('module');
  const [uploadedAssetIds, setUploadedAssetIds] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [applying, setApplying] = useState(false);
  const moduleFields = moduleDefinition?.fields || [];
  const featureDefinitions = moduleDefinition?.features || [];

  useEffect(() => {
    if (!moduleData || !show) return;
    const data = clone(moduleData.data || {});
    if (moduleData.type === 'main-cards-showcase') data.classRenames = { ...(data.classRenames || {}) };
    const features = clone(moduleData.features || []);
    setCurrentData(data);
    setCurrentFeatures(features);
    setActivePanel(moduleFields.length ? 'module' : (features[0]?.id || 'module'));
    setUploadedAssetIds([]);
    setInitialSnapshot(JSON.stringify({ data, features }));
    setApplying(false);
  }, [moduleData, moduleFields.length, show]);

  const isDirty = useMemo(
    () => Boolean(initialSnapshot) && initialSnapshot !== JSON.stringify({ data: currentData, features: currentFeatures }),
    [currentData, currentFeatures, initialSnapshot],
  );
  const activeFeatureIndex = currentFeatures.findIndex((feature) => feature.id === activePanel);
  const activeFeature = activeFeatureIndex >= 0 ? currentFeatures[activeFeatureIndex] : null;
  const activeFeatureDefinition = activeFeature ? featureDefinition(moduleDefinition, activeFeature.type) : null;

  const customClasses = useMemo(() => {
    if (moduleData?.type !== 'main-cards-showcase') return [];
    const classes = new Map();
    (currentData.cards || []).filter((card) => isCustomClass(card?.class)).forEach((card) => {
      const raw = card.class.trim();
      const key = raw.toLowerCase();
      if (!classes.has(key)) classes.set(key, { key, raw, suggested: customClassLabel(raw) });
    });
    return [...classes.values()];
  }, [currentData.cards, moduleData?.type]);

  useEffect(() => {
    if (moduleData?.type !== 'main-cards-showcase') return;
    const valid = new Set(customClasses.map((item) => item.key));
    setCurrentData((previous) => {
      const existing = Object.fromEntries(Object.entries(previous.classRenames || {}).map(([key, value]) => [key.toLowerCase(), value]));
      const next = Object.fromEntries(customClasses.map((item) => [item.key, String(existing[item.key] || '').trim() || item.suggested]));
      const cleaned = Object.fromEntries(Object.entries(next).filter(([key]) => valid.has(key)));
      return JSON.stringify(cleaned) === JSON.stringify(previous.classRenames || {}) ? previous : { ...previous, classRenames: cleaned };
    });
  }, [customClasses, moduleData?.type]);

  const registerUploadedAsset = (assetId) => setUploadedAssetIds((ids) => ids.includes(assetId) ? ids : [...ids, assetId]);
  const updateModuleField = (key, value) => setCurrentData((data) => ({ ...data, [key]: value }));
  const updateFeatureField = (key, value) => setCurrentFeatures((features) => features.map((feature) =>
    feature.id === activePanel ? { ...feature, data: { ...feature.data, [key]: value } } : feature));

  const addFeature = (type) => {
    const definition = featureDefinition(moduleDefinition, type);
    if (!definition) return;
    const count = currentFeatures.filter((feature) => feature.type === type).length;
    if (definition.max && count >= definition.max) return;
    const feature = { id: uuid(), type, data: defaultsFrom(definition.fields) };
    setCurrentFeatures((features) => [...features, feature]);
    setActivePanel(feature.id);
  };

  const deleteFeature = (featureId) => {
    const index = currentFeatures.findIndex((feature) => feature.id === featureId);
    if (index < 0) return;
    const feature = currentFeatures[index];
    const definition = featureDefinition(moduleDefinition, feature.type);
    if (!window.confirm(`Delete “${featureName(feature, definition, index)}”?`)) return;
    const next = currentFeatures.filter((item) => item.id !== featureId);
    setCurrentFeatures(next);
    if (activePanel === featureId) setActivePanel(next[Math.min(index, next.length - 1)]?.id || (moduleFields.length ? 'module' : 'module'));
  };

  const moveFeature = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= currentFeatures.length) return;
    const next = [...currentFeatures];
    [next[index], next[destination]] = [next[destination], next[index]];
    setCurrentFeatures(next);
  };

  const reorderFeatures = ({ source, destination }) => {
    if (!destination || source.index === destination.index) return;
    const next = [...currentFeatures];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    setCurrentFeatures(next);
  };

  const collectReferencedAssets = () => {
    const ids = new Set();
    const visit = (value) => {
      if (!value || typeof value !== 'object') return;
      if (value.assetId) ids.add(Number(value.assetId));
      Object.values(value).forEach(visit);
    };
    visit(currentData); visit(currentFeatures);
    return ids;
  };

  const save = async () => {
    setApplying(true);
    const referenced = collectReferencedAssets();
    await Promise.all(uploadedAssetIds.filter((id) => !referenced.has(Number(id)))
      .map((id) => discardSetImage(setId, id).catch(() => undefined)));
    handleSave({ id: moduleData.id, type: moduleData.type, data: currentData, features: currentFeatures });
    setUploadedAssetIds([]);
  };

  const cancel = async () => {
    if (applying) return;
    if (isDirty && !window.confirm('Discard the changes made in this editor?')) return;
    setApplying(true);
    await Promise.all(uploadedAssetIds.map((id) => discardSetImage(setId, id).catch(() => undefined)));
    setUploadedAssetIds([]);
    handleClose();
  };

  if (!moduleDefinition || !moduleData) return null;
  const featureCountLabel = `${currentFeatures.length} feature${currentFeatures.length === 1 ? '' : 's'}`;

  return (
    <Modal show={show} onHide={cancel} size="xl" fullscreen="md-down" backdrop="static" centered dialogClassName="set-module-editor-modal">
      <Modal.Header closeButton={!applying}>
        <div><span className="set-editor-kicker">Editing section</span><Modal.Title>{moduleDefinition.name}</Modal.Title><p>{moduleDefinition.description}</p></div>
      </Modal.Header>
      <Modal.Body>
        <div className={`set-module-editor-layout ${featureDefinitions.length ? '' : 'set-without-features'}`}>
          {featureDefinitions.length > 0 && <div className="set-mobile-feature-bar">
            <Form.Select size="sm" value={activePanel} onChange={(event) => setActivePanel(event.target.value)} aria-label="Choose editor panel">
              {moduleFields.length > 0 && <option value="module">Section settings</option>}
              {currentFeatures.map((feature, index) => <option key={feature.id} value={feature.id}>{featureName(feature, featureDefinition(moduleDefinition, feature.type), index)}</option>)}
            </Form.Select>
            <Dropdown align="end">
              <Dropdown.Toggle size="sm" variant="primary" className="set-mobile-add-toggle" aria-label="Add feature"><BsPlusLg /></Dropdown.Toggle>
              <Dropdown.Menu>{featureDefinitions.map((definition) => { const count = currentFeatures.filter((feature) => feature.type === definition.type).length; const full = Boolean(definition.max && count >= definition.max); return <Dropdown.Item key={definition.type} disabled={full} onClick={() => addFeature(definition.type)}>Add {definition.title}</Dropdown.Item>; })}</Dropdown.Menu>
            </Dropdown>
            {activeFeature && <Dropdown align="end">
              <Dropdown.Toggle size="sm" variant="light" className="set-mobile-feature-menu" aria-label="Feature actions"><BsThreeDotsVertical /></Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => moveFeature(activeFeatureIndex, -1)} disabled={activeFeatureIndex === 0}>Move earlier</Dropdown.Item>
                <Dropdown.Item onClick={() => moveFeature(activeFeatureIndex, 1)} disabled={activeFeatureIndex === currentFeatures.length - 1}>Move later</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item className="text-danger" onClick={() => deleteFeature(activeFeature.id)}>Delete feature</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>}
          </div>}
          {featureDefinitions.length > 0 && (
            <aside className="set-feature-sidebar">
              {moduleFields.length > 0 && (
                <button type="button" className={`set-editor-nav-item ${activePanel === 'module' ? 'set-is-active' : ''}`} onClick={() => setActivePanel('module')}>
                  <span className="set-editor-nav-icon"><BsGear /></span><span><strong>Section settings</strong><small>Layout and general content</small></span>
                </button>
              )}
              <div className="set-feature-sidebar-heading"><span>Features</span><small>{featureCountLabel}</small></div>
              <DragDropContext onDragEnd={reorderFeatures}>
                <Droppable droppableId="set-feature-list">
                  {(provided) => (
                    <div className="set-feature-list" ref={provided.innerRef} {...provided.droppableProps}>
                      {currentFeatures.map((feature, index) => {
                        const definition = featureDefinition(moduleDefinition, feature.type);
                        const label = featureName(feature, definition, index);
                        return (
                          <Draggable key={feature.id} draggableId={feature.id} index={index}>
                            {(drag) => (
                              <div ref={drag.innerRef} {...drag.draggableProps} style={drag.draggableProps.style} className={`set-feature-nav-row ${activePanel === feature.id ? 'set-is-active' : ''}`}>
                                <button type="button" className="set-feature-drag" {...drag.dragHandleProps} aria-label={`Reorder ${label}`}><BsGripVertical /></button>
                                <button type="button" className="set-feature-select" onClick={() => setActivePanel(feature.id)}><span>{label}</span><small>{definition?.group ? `${definition.group} · ` : ''}{definition?.title || feature.type}</small></button>
                                <button type="button" className="set-feature-delete" onClick={() => deleteFeature(feature.id)} aria-label={`Delete ${label}`}><BsTrash /></button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {!currentFeatures.length && <div className="set-feature-empty"><p>No features yet.</p><small>Add one below to start building this section.</small></div>}
              <Dropdown className="set-add-feature">
                <Dropdown.Toggle variant="primary" className="w-100"><BsPlusLg /> Add feature</Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {featureDefinitions.map((definition) => {
                    const count = currentFeatures.filter((feature) => feature.type === definition.type).length;
                    const full = Boolean(definition.max && count >= definition.max);
                    return <Dropdown.Item key={definition.type} disabled={full} onClick={() => addFeature(definition.type)}><strong>{definition.title}</strong><small>{definition.max ? `${count} of ${definition.max}` : `${count} added`}{full ? ' · Maximum reached' : ''}</small></Dropdown.Item>;
                  })}
                </Dropdown.Menu>
              </Dropdown>
            </aside>
          )}

          <section className="set-editor-workspace">
            {activePanel === 'module' ? (
              <div className="set-editor-form-panel">
                <div className="set-editor-form-heading"><div><span>Section</span><h3>General settings</h3><p>These settings affect the entire {moduleDefinition.name.toLowerCase()} section.</p></div></div>
                {moduleFields.length ? <Form><EditorFields fields={moduleFields} values={currentData} onChange={updateModuleField} setId={setId} onAssetUploaded={registerUploadedAsset} /></Form> : <div className="set-editor-empty-workspace"><BsGear /><h4>No general settings</h4><p>Choose a feature from the sidebar or add a new one.</p></div>}
                {customClasses.length > 0 && (
                  <div className="set-custom-class-names"><h4>Custom class detected</h4><p>These are internal class keys. Give them the names visitors should see when cards are grouped by class.</p>{customClasses.map((item) => <Form.Group key={item.key} className="mb-3"><Form.Label><code>{item.raw}</code></Form.Label><Form.Control value={currentData.classRenames?.[item.key] || ''} onChange={(event) => updateModuleField('classRenames', { ...(currentData.classRenames || {}), [item.key]: event.target.value })} placeholder={item.suggested} /></Form.Group>)}</div>
                )}
              </div>
            ) : activeFeature && activeFeatureDefinition ? (
              <div className="set-editor-form-panel">
                <div className="set-editor-form-heading set-feature-heading"><div><span>{activeFeatureDefinition.title} {activeFeatureIndex + 1}</span><h3>{featureName(activeFeature, activeFeatureDefinition, activeFeatureIndex)}</h3><p>Edit the content for this feature. Reorder or delete it from the feature list.</p></div></div>
                <Form><EditorFields fields={activeFeatureDefinition.fields || []} values={activeFeature.data} onChange={updateFeatureField} setId={setId} onAssetUploaded={registerUploadedAsset} /></Form>
              </div>
            ) : (
              <div className="set-editor-empty-workspace"><BsPlusLg /><h4>Add your first feature</h4><p>Use “Add feature” in the sidebar to begin.</p></div>
            )}
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer><span className={`set-editor-change-status ${isDirty ? 'set-has-changes' : ''}`}>{isDirty ? 'Unsaved changes' : 'No changes yet'}</span><Button variant="outline-secondary" onClick={cancel} disabled={applying}>Cancel</Button><Button variant="primary" onClick={save} disabled={applying || !isDirty}>{applying && <Spinner size="sm" className="me-2" />}Save section</Button></Modal.Footer>
    </Modal>
  );
}
